import type { RunSummary } from '@shared/types/factory';
import { isRunActive, runRef } from '@/components/factory/run-status';

/**
 * SF-T-237: what Merge says when a run is still going that may push more to the PR after it's merged. A run
 * working or queued warns; one waiting for people (a live run parked after its turn, or one waiting for
 * answers) says so more gently. Merge anyway stays allowed.
 */

export interface MergeRunWarning {
  run: Pick<RunSummary, 'id' | 'number'>;
  /** `warning` while it's working or queued; `info` while it waits for people. */
  tone: 'warning' | 'info';
  title: string;
  text: string;
}

export type MergeRunFields = Pick<RunSummary, 'id' | 'number' | 'status' | 'live' | 'live_parked_at'> &
  Partial<Pick<RunSummary, 'task' | 'subtasks' | 'branch' | 'pr_url'>>;

/**
 * The unfinished runs that may push to this PR: the task's own (or a run building it as a sub-task), and any
 * on the PR's branch or the PR itself, such as a sub-task's run pushing to the PR it shares with its parent.
 * The server takes "same branch or same PR" as the same work too (runs/start.ts, a takeover's check).
 */
export function runsOnPr<R extends MergeRunFields>(runs: readonly R[] | null | undefined, on: { taskId: string; prUrl: string; headRef?: string | null }): R[] {
  return (runs ?? []).filter(
    (r) =>
      isRunActive(r.status) &&
      (r.task?.id === on.taskId ||
        Boolean(r.subtasks?.some((s) => s.id === on.taskId)) ||
        r.pr_url === on.prUrl ||
        Boolean(on.headRef && r.branch === on.headRef))
  );
}

/**
 * SF-T-237: the note on a Merge button while a run is going on the task, before it's pressed (its hint, and
 * the icon's tone), or null with none. The dialog still checks again, the PR's branch included.
 */
export function mergeRunNote(run: MergeRunFields | null | undefined): { tone: MergeRunWarning['tone']; text: string } | null {
  const warning = mergeRunWarning(run ? [run] : null);
  return warning ? { tone: warning.tone, text: `${warning.title}: it may push more to this pull request` } : null;
}

/** Waiting for people: a live run parked after its turn, or a run waiting for answers or an approval. */
function waitsForPeople(run: MergeRunFields): boolean {
  return Boolean(run.live && run.live_parked_at) || run.status === 'waiting';
}

/**
 * The warning for the unfinished runs (newest first), or null when none is going. `taskId` is the task
 * being merged: a run on another task (a sub-task sharing the PR) is named with that task's ref.
 */
export function mergeRunWarning(runs: readonly MergeRunFields[] | null | undefined, taskId?: string): MergeRunWarning | null {
  const active = (runs ?? []).filter((r) => isRunActive(r.status));
  if (!active.length) return null;
  // A run that's working outweighs one waiting for people.
  const run = active.find((r) => !waitsForPeople(r)) ?? active[0]!;
  const ref = runRef(run.number);
  const other = taskId && run.task && run.task.id !== taskId && !run.subtasks?.some((s) => s.id === taskId) ? run.task.ref : null;
  const where = other ? `on ${other}, which shares this pull request` : 'on this task';
  if (!waitsForPeople(run)) {
    return run.status === 'queued'
      ? {
          run,
          tone: 'warning',
          title: `${ref} is queued ${where}`,
          text: 'Once it starts it may push more to this pull request, which merging now would leave out.',
        }
      : {
          run,
          tone: 'warning',
          title: `${ref} is still working ${where}`,
          text: 'It may push more to this pull request, which merging now would leave out. Wait for it to finish, or merge anyway.',
        };
  }
  return run.live && run.live_parked_at
    ? {
        run,
        tone: 'info',
        title: `${ref} is still open ${where}, waiting for you`,
        text: "A reply would have it push more to this pull request. If you're done with it, wrap it up first.",
      }
    : {
        run,
        tone: 'info',
        title: `${ref} is waiting for answers ${where}`,
        text: 'Once it has them it may push more to this pull request.',
      };
}
